import type { Cluster } from "@googlemaps/markerclusterer";

import { onBeforeUnmount, provide, reactive, toRaw } from "vue";

import {
  createSpiderfyController,
  type IMapMarkerOffset,
  type ISpiderfyCluster,
  type ISpiderfyMarker,
} from "@/components/ui/map/map-spiderfy";
import {
  type IMapSpiderfyMarkerRegistry,
  mapSpiderfyMarkerRegistryKey,
} from "@/components/ui/map/map-spiderfy-context";

const DEFAULT_MARKER_OFFSET: IMapMarkerOffset = { x: 0, y: 0 };

interface IUseMapSpiderfyOptions {
  fallbackMarkerWidth: number;
}

export function useMapSpiderfy(options: IUseMapSpiderfyOptions) {
  const registrations = new Map<ISpiderfyMarker, { data: object; getWidth: () => number }>();
  const offsets = reactive(new Map<object, IMapMarkerOffset>());
  let reconciliationRevision = 0;

  const markerRegistry: IMapSpiderfyMarkerRegistry = {
    register(data, marker, getWidth) {
      registrations.set(marker, { data, getWidth });
    },
    unregister(marker) {
      registrations.delete(marker);
    },
  };
  provide(mapSpiderfyMarkerRegistryKey, markerRegistry);

  const controller = createSpiderfyController({
    resolveMarker(marker) {
      const registration = registrations.get(marker);
      if (!registration) {
        return undefined;
      }

      return {
        data: registration.data,
        width: registration.getWidth() || options.fallbackMarkerWidth,
      };
    },
    setOffset(data, offset) {
      const rawData = toRaw(data);
      if (offset) {
        offsets.set(rawData, offset);
      } else {
        offsets.delete(rawData);
      }
    },
  });

  function toSpiderfyCluster(cluster: Cluster) {
    // vue3-google-map registers CustomMarker OverlayViews in MarkerClusterer even though
    // MarkerClusterer's Cluster type only declares native and advanced Google markers.
    return cluster as unknown as ISpiderfyCluster;
  }

  function close() {
    reconciliationRevision++;
    controller.close();
  }

  function getOffset(data: object) {
    return offsets.get(toRaw(data)) ?? DEFAULT_MARKER_OFFSET;
  }

  function isClusterActive(cluster: Cluster) {
    return controller.matches(toSpiderfyCluster(cluster));
  }

  function open(cluster: Cluster, map: google.maps.Map) {
    return controller.open(toSpiderfyCluster(cluster), map);
  }

  function reconcileClusters(clusters: Array<Cluster>, map: google.maps.Map) {
    const currentRevision = ++reconciliationRevision;
    if (clusters.some((cluster) => controller.refresh(toSpiderfyCluster(cluster), map))) {
      return;
    }

    queueMicrotask(() => {
      if (currentRevision === reconciliationRevision) {
        controller.discard();
      }
    });
  }

  onBeforeUnmount(close);

  return {
    close,
    getOffset,
    isClusterActive,
    open,
    reconcileClusters,
  };
}
